'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlovLexUrl } from '@/lib/config';
import {
  parseFileIdHref,
  parseStorageNodeHref,
} from '@/lib/utils/markdownUtils';
import storageService from '@/services/storage-service';
import userFilesServices from '@/services/user-files-services';
import { parseDocumentUrl } from '@/utils/previewUtils';
import { Check, Copy, ExternalLink, Link2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

type LinkActionMode = 'copy' | 'open';

interface LinkActionsButtonProps {
  href: string;
  sectionId?: string | null | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  originalSource?: string | null | undefined; // URL from backend API (for external slov-lex.sk links)
  mode: LinkActionMode; // 'copy' = copy to clipboard, 'open' = open in new tab
}

export function LinkActionsButton({
  href,
  sectionId,
  onOpenChange,
  originalSource,
  mode,
}: LinkActionsButtonProps) {
  const t = useTranslations('CopyLinkButton');
  const previewT = useTranslations('TextPreview');
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Notify parent when dropdown state changes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    handleOpenChange(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      handleOpenChange(false);
    }, 100);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[LinkActionsButton] Failed to copy:', err);
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const executeAction = async (url: string) => {
    if (mode === 'copy') {
      await copyToClipboard(url);
    } else {
      openInNewTab(url);
    }
  };

  const handleInternalLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const parsedFileId = parseFileIdHref(href);
    if (parsedFileId) {
      try {
        const response = await userFilesServices.getFile(parsedFileId.fileId);
        const file = response.data;
        if (file?.download_url) {
          await executeAction(`${file.download_url}${parsedFileId.fragment}`);
        } else {
          alert(previewT('noPdfAvailable'));
        }
      } catch (err) {
        console.error(
          '[LinkActionsButton] Failed to resolve file_id link',
          err
        );
        alert(previewT('noPdfAvailable'));
      }
      handleOpenChange(false);
      return;
    }

    const parsedStorageNode = parseStorageNodeHref(href);
    if (parsedStorageNode) {
      try {
        const response = await storageService.getNode(parsedStorageNode.nodeId);
        const node = response.data;
        if (node?.download_url) {
          await executeAction(
            `${node.download_url}${parsedStorageNode.fragment}`
          );
        } else {
          alert(previewT('noPdfAvailable'));
        }
      } catch (err) {
        console.error(
          '[LinkActionsButton] Failed to resolve storage_node link',
          err
        );
        alert(previewT('noPdfAvailable'));
      }
      handleOpenChange(false);
      return;
    }

    // href already contains the full URL with hash (if present), so use it directly
    await executeAction(href);
    // Close dropdown after action
    handleOpenChange(false);
  };

  const handleExternalLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Use original_source from backend API if available
    if (originalSource) {
      // Check if originalSource already contains a hash to avoid duplication
      const externalUrl =
        sectionId && !originalSource.includes('#')
          ? `${originalSource}#${sectionId}`
          : originalSource;
      await executeAction(externalUrl);
      // Close dropdown after action
      handleOpenChange(false);
      return;
    }

    // Fallback: manually construct URL (for backwards compatibility)
    const parsed = parseDocumentUrl(href);

    if (parsed.iri) {
      // Legislation document: use IRI path
      const path = sectionId ? `${parsed.iri}#${sectionId}` : parsed.iri;
      const externalUrl = `${SlovLexUrl}${path}`;
      await executeAction(externalUrl);
      // Close dropdown after action
      handleOpenChange(false);
    } else {
      // Decision document or unknown: no external link available
      alert(t('noExternalLink'));
      handleOpenChange(false);
    }
  };

  // Determine icon based on mode
  const TriggerIcon = () => {
    if (mode === 'copy') {
      return copied ? (
        <Check className='h-3.5 w-3.5 text-status-success' />
      ) : (
        <Copy className='h-3.5 w-3.5 text-brand-tertiary dark:text-brand-tertiary-color' />
      );
    }
    return (
      <ExternalLink className='h-3.5 w-3.5 text-brand-tertiary dark:text-brand-tertiary-color' />
    );
  };

  const ariaLabel = mode === 'copy' ? t('copyLink') : t('openLink');

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <div
          className='h-5 w-5 flex items-center justify-center hover:bg-brand-tertiary/10 dark:hover:bg-brand-tertiary-color/10 rounded cursor-pointer'
          aria-label={ariaLabel}
          onPointerEnter={handleMouseEnter}
          onPointerLeave={handleMouseLeave}
          onClick={handleClick}
        >
          <TriggerIcon />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='w-56'
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeave}
      >
        <DropdownMenuItem onClick={handleInternalLink}>
          <Link2 className='mr-2 h-4 w-4' />
          <span>{t('internalLink')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExternalLink}>
          <ExternalLink className='mr-2 h-4 w-4' />
          <span>{t('externalLink')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
